import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Loader } from 'theSource/components';
import {
  DecisionIQMutation,
  DecisionIQSurveyQuestionDto,
  DecisionIQSurveySubmissionDto,
  useDecisionIQSurveyQuery,
} from 'enrollment/queries';
import { DependentsResponseDto } from 'theSource/features/FamilyMembers/types';
import { SubscriberFieldsDto } from 'theSource/types';
import { DecisionIQInfoBottomStyled } from '../../DecisionIQStyled';
import { PsCheckboxTuner } from 'stencilSetup';
import { FamilyMember } from 'enrollment/features/Shared';
import { insertOrRemoveValues } from '../..';
import { useConfigurationQuery } from 'theSource/queries';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { DrawerDialog } from 'enrollment/components';

export interface DecisionIQDentalSurveyProps {
  orgBenefitId: number;
  subscriber?: SubscriberFieldsDto;
  dependents?: DependentsResponseDto;
  standalonePage?: boolean;
  isModalSurvey?: boolean;
  onSubmit?: () => void;
  onRecommendationSurveySubmit?: (arg0: DecisionIQSurveySubmissionDto) => void;
}

export const DecisionIQDentalSurvey = ({
  orgBenefitId,
  subscriber,
  dependents,
  standalonePage = false,
  isModalSurvey = false,
  onSubmit,
  onRecommendationSurveySubmit,
}: DecisionIQDentalSurveyProps) => {
  const { t } = useTranslation('pages.decisionIQ');
  const enrollmentContext = useSelector(selectEnrollmentContext);
  const [dentalQuestions, setDentalQuestions] = useState<
    DecisionIQSurveyQuestionDto[]
  >([]);
  const [showDentalDeps, setShowDentalDeps] = useState<boolean[]>();
  const [coveredDependents, setCoveredDependents] = useState<
    DecisionIQSurveyQuestionDto[]
  >([]);
  const excludedQuestions = ['dental_save_information', 'covered_dependents'];
  // Initialize an array of boolean values for the open state of each drawer
  const [isDentalDrawerOpen, setIsDentalDrawerOpen] = useState(
    new Array(dentalQuestions.length).fill(false)
  );

  const { configuration } = useConfigurationQuery();
  const {
    decisionIQSurveyData,
    isFetchingDecisionIQSurveys,
    isLoadingDecisionIQSurveys,
  } = useDecisionIQSurveyQuery({
    orgBenefitId: orgBenefitId,
    enabled: orgBenefitId > 0,
  });

  const updateFormValues = (value: any) => {
    // [0]: questionCode, [1]: dependentId || checkbox, [2]: questionIndex (if checking question)
    const keyValues = value.split('-');
    const newQuestionResponses = [...dentalQuestions];
    const qIndex = dentalQuestions.findIndex((question) => {
      return question.question_code === keyValues[0];
    });

    // user has unchecked question, we need to set is_checked? to []
    if (keyValues[1] === 'checkbox') {
      newQuestionResponses[Number(keyValues[2])].options[0]['is_checked?'] = [];
      setDentalQuestions(newQuestionResponses);
      return;
    }

    // we need to essentially push or pop values from the is_checked array
    const oldIsCheckedArr = dentalQuestions[qIndex].options[0]['is_checked?'];
    newQuestionResponses[qIndex].options[0]['is_checked?'] =
      insertOrRemoveValues(keyValues[1], oldIsCheckedArr);
    setDentalQuestions(newQuestionResponses);
  };

  const handleSubmitIntermediary = () => {
    const submitBtn = document.getElementById(
      'deciq-dent-submit'
    ) as HTMLElement;

    if (isModalSurvey) {
      setTimeout(() => {
        submitBtn.click();
      }, 750);
    } else {
      submitBtn.click();
    }
  };

  const handleQuestionSelect = (e: any) => {
    if (!showDentalDeps || e.target.tagName !== 'INPUT') {
      return;
    }
    const index = Number(e.target.id.split('-')[2]);
   
    if (isMobileView) {
      const newIsDentalDrawerOpen = [...isDentalDrawerOpen];
      newIsDentalDrawerOpen[index] = true;
      updateFormValues(e.target.name);
      setIsDentalDrawerOpen(newIsDentalDrawerOpen);
    } else {
      // Update isDentalDrawerOpen state
      const newShowDentalDeps = [...showDentalDeps];
      newShowDentalDeps[index] = !newShowDentalDeps[index];
      setShowDentalDeps(newShowDentalDeps);
      updateFormValues(e.target.name);
    }
  };

  const { SurveyResponsesRequest } = DecisionIQMutation(
    orgBenefitId,
    String(configuration?.apiHost)
  );

  const handleSubmit = () => {
    if (onRecommendationSurveySubmit) {
      onRecommendationSurveySubmit({
        dependent_ids: dependents
          ? dependents.data.dependents.map((dependent) => dependent.id)
          : undefined,
        questions: [...coveredDependents, ...dentalQuestions],
        life_event_completed: false,
        enrollment_context_type: enrollmentContext,
      });
    } else {
      SurveyResponsesRequest({
        dependent_ids: dependents
          ? dependents.data.dependents.map((dependent) => dependent.id)
          : undefined,
        questions: [...coveredDependents, ...dentalQuestions],
        life_event_completed: false,
        enrollment_context_type: enrollmentContext,
      });
    }
    if (onSubmit) {
      onSubmit();
    }
  };

  useEffect(() => {
    if (
      decisionIQSurveyData &&
      !(decisionIQSurveyData.data.questions.length === undefined)
    ) {
      const dentalQs = decisionIQSurveyData.data.questions.filter(
        (question) => {
          return (
            question.question_type === 'checkboxes' &&
            !excludedQuestions.includes(question.question_code)
          );
        }
      );

      setShowDentalDeps(
        dentalQs.map((question) =>
          Boolean(question.options[0]['is_checked?'].length > 0)
        )
      );
      setDentalQuestions(dentalQs);
      setCoveredDependents([...decisionIQSurveyData.data.questions.slice(12)]);

      // need to disable; updating state and then using a local varibale was causing endless re-render
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [decisionIQSurveyData]);

  useEffect(() => {
    const attachSubmitListener = (submitButton: HTMLElement | null) => {
      if (
        (standalonePage || isModalSurvey) &&
        !submitButton?.hasAttribute('listening-dent')
      ) {
        submitButton?.addEventListener('click', handleSubmitIntermediary);
        submitButton?.setAttribute('listening-dent', '');
      }
    };

    let submitButton;

    if (isModalSurvey) {
      setTimeout(() => {
        submitButton = document.getElementById('decisionIQSurveyDrawer_submit');
        attachSubmitListener(submitButton);
      }, 500);
    } else {
      submitButton = document.getElementById('enrollment-continue-button');
      attachSubmitListener(submitButton);
    }

    // we need to ensure we are only attaching the submit listener ONCE
  });

  if (isFetchingDecisionIQSurveys || isLoadingDecisionIQSurveys) {
    return <Loader />;
  }

  if (
    !decisionIQSurveyData ||
    decisionIQSurveyData.data.questions.length === undefined
  ) {
    return <></>;
  }

  const isMobileView = window.innerWidth < 744;

  return (
    <fieldset
      className="row mb-4 mt-2 pt-4 border-top"
      data-testid="decision-iq-dent-survey"
    >
      <div className="col">
        <legend className="body1 mb-0">{t('dental_family_select')}</legend>
        <DecisionIQInfoBottomStyled>
          {dentalQuestions.map((question, index) => (
            <div key={index}>
              <PsCheckboxTuner
                data-testid={`dental-checkbox-${index}`}
                inputId={`dental-checkbox-${index}`}
                name={`dental-checkbox-${index}`}
                label={String(question.question)}
                onClick={handleQuestionSelect}
                checked={showDentalDeps && showDentalDeps[index]}
                key={question.question_id}
                labelClass={
                  question.options[0]['is_checked?'].length > 0 ? 'checked' : ''
                }
              />
              {isMobileView
                ? isDentalDrawerOpen &&
                  isDentalDrawerOpen[index] && (
                    <DrawerDialog
                      id={`dental-checkbox-${index}-drawer`}
                      openDialog={isDentalDrawerOpen[index]}
                      closeDialog={() => {
                        setIsDentalDrawerOpen(
                          new Array(dentalQuestions.length).fill(false)
                        );
                      }}
                      okButtonLabel="Save"
                      closeButtonLabel="Cancel"
                      dialogHeading={String(question.question)}
                    >
                      <div
                        className="py-3"
                        key={`${question.question_id}_${index}`}
                      >
                        {/* Cannot use "isYou" since that makes the checkmark disabled, which we don't want */}
                        <FamilyMember
                          name="You"
                          onChangeKey={`${question.question_code}-emp`}
                          onChangeWithKey={updateFormValues}
                          profile={subscriber?.subscriber_profile}
                          selected={question.options[0]['is_checked?'].includes(
                            'emp'
                          )}
                        />
                        {dependents &&
                          dependents.data.dependents.map((dependent, index) => (
                            <FamilyMember
                              key={index}
                              name={dependent.first_name}
                              onChangeKey={`${question.question_code}-${dependent.id}`}
                              onChangeWithKey={updateFormValues}
                              profile={dependent.dependent_profile}
                              selected={question.options[0][
                                'is_checked?'
                              ].includes(String(dependent.id))}
                            />
                          ))}
                      </div>
                    </DrawerDialog>
                  )
                : showDentalDeps &&
                  showDentalDeps[index] && (
                    <div
                      className="py-3 ml-4_5"
                      key={`${question.question_id}_${index}`}
                    >
                      {/* Cannot use "isYou" since that makes the checkmark disabled, which we don't want */}
                      <FamilyMember
                        name="You"
                        onChangeKey={`${question.question_code}-emp`}
                        onChangeWithKey={updateFormValues}
                        profile={subscriber?.subscriber_profile}
                        selected={question.options[0]['is_checked?'].includes(
                          'emp'
                        )}
                      />
                      {dependents &&
                        dependents.data.dependents.map((dependent, index) => (
                          <FamilyMember
                            key={index}
                            name={dependent.first_name}
                            onChangeKey={`${question.question_code}-${dependent.id}`}
                            onChangeWithKey={updateFormValues}
                            profile={dependent.dependent_profile}
                            selected={question.options[0][
                              'is_checked?'
                            ].includes(String(dependent.id))}
                          />
                        ))}
                    </div>
                  )}
            </div>
          ))}
        </DecisionIQInfoBottomStyled>
        <button
          id="deciq-dent-submit"
          onClick={() => handleSubmit()}
          style={{ display: 'none' }}
        ></button>
      </div>
    </fieldset>
  );
};
